import { useEffect, useRef, useState } from "react";
import { FaAndroid, FaArrowRight, FaDownload, FaGooglePlay, FaPause, FaPlay, FaTimes } from "react-icons/fa";

interface DemoMedia {
  filename: string;
  audioName: string;
  title: string;
  durationSeconds: number;
}

interface DemoResponse { media: DemoMedia[]; }

const PLAYSTORE_URL = "https://play.google.com/store/apps/details?id=com.getvak.signals.acr_offline_demo";

function formatTime(seconds: number) {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secondsPart = String(total % 60).padStart(2, "0");
  return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${secondsPart}` : `${minutes}:${secondsPart}`;
}

export function PublicDemoPage() {
  const [data, setData] = useState<DemoResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeFile, setActiveFile] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [betaOpen, setBetaOpen] = useState(false);
  const [betaEmail, setBetaEmail] = useState("");
  const [betaError, setBetaError] = useState<string | null>(null);
  const [submittingBeta, setSubmittingBeta] = useState(false);
  const [apkOpen, setApkOpen] = useState(false);
  const audioRefs = useRef(new Map<string, HTMLAudioElement>());
  const totalDuration = data?.media.reduce((total, media) => total + media.durationSeconds, 0) ?? 0;

  useEffect(() => {
    fetch("/api/public-demo/media")
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error ?? "Could not load the demo library.");
        return payload as DemoResponse;
      })
      .then(setData)
      .catch((requestError) => setError((requestError as Error).message));
  }, []);

  useEffect(() => () => audioRefs.current.forEach((audio) => audio.pause()), []);

  async function toggle(media: DemoMedia) {
    const audio = audioRefs.current.get(media.filename);
    if (!audio) return;
    if (activeFile === media.filename) {
      audio.pause();
      setActiveFile(null);
      return;
    }
    audioRefs.current.forEach((other, filename) => {
      if (filename !== media.filename) other.pause();
    });
    try {
      await audio.play();
      setActiveFile(media.filename);
      setElapsed(audio.currentTime);
    } catch {
      setError("Audio playback is unavailable in this browser.");
    }
  }

  async function joinBeta(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!event.currentTarget.reportValidity()) return;
    setSubmittingBeta(true);
    setBetaError(null);
    try {
      const response = await fetch("/api/play-store-beta-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: betaEmail }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error ?? "Could not submit your beta request.");
      window.location.assign(PLAYSTORE_URL);
    } catch (requestError) {
      setBetaError((requestError as Error).message);
      setSubmittingBeta(false);
    }
  }

  return (
    <div className="public-demo-page">
      <header className="public-demo-header">
        <a className="public-demo-brand" href="/"><span className="public-demo-logo"><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M21 12h-2c-.894 0-1.662-.857-1.761-2-.296-3.45-.749-6-2.749-6s-2.5 3.582-2.5 8-.5 8-2.5 8-2.452-2.547-2.749-6C7.141 12.857 6.373 12 5.477 12h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></span>Signals</a>
        <div className="public-demo-header-actions">
          {/* <button className="public-demo-apk" type="button" onClick={() => setApkOpen(true)}><FaAndroid /> Download APK</button> */}
          <button className="public-demo-store" type="button" onClick={() => setBetaOpen(true)}><FaGooglePlay /> Get Android Demo App</button>
        </div>
      </header>
      <main className="public-demo-main">
        <section className="public-demo-hero">
          <span className="public-demo-eyebrow">LIVE SAMPLE LIBRARY</span>
          <h1>Hear what Signals recognizes.</h1>
          <p>Want to try Signals yourself? Play any sample on this page while the app is listening, and watch the matching track appear on your device.</p>
          <div className="public-demo-how-to">
            <strong>How to test</strong>
            <ol>
              <li>Join the beta, install Signals, allow microphone access, then tap <b>Start Listening</b>.</li>
              <li>Come back here and play any clip. Its matching title will appear under <b>Recent Detections</b> in the app.</li>
            </ol>
          </div>
        </section>
        <section className="public-demo-library" aria-labelledby="demo-library-title">
          <div className="public-demo-library-head">
            <div><h2 id="demo-library-title">Demo audio library</h2><p>Short audio creatives included in the public demo catalog.</p></div>
            {data && <span>{data.media.length} files · {formatTime(totalDuration)} total</span>}
          </div>
          {error && <p className="public-demo-error">{error}</p>}
          {!data && !error && <p className="public-demo-loading">Loading demo audio…</p>}
          {data && <div className="public-demo-table-wrap"><table className="public-demo-table">
            <thead><tr><th>Audio</th><th>Duration</th><th aria-label="Playback" /></tr></thead>
            <tbody>{data.media.map((media) => {
              const active = activeFile === media.filename;
              return <tr key={media.filename} className={active ? "playing" : ""}>
                <td><b>{media.title}</b></td>
                <td className="public-demo-duration">{formatTime(media.durationSeconds)}</td>
                <td className="public-demo-playback">
                  <audio
                    ref={(element) => { if (element) audioRefs.current.set(media.filename, element); else audioRefs.current.delete(media.filename); }}
                    src={`/api/public-demo/media/${encodeURIComponent(media.audioName)}`}
                    preload="none"
                    onTimeUpdate={(event) => { if (activeFile === media.filename) setElapsed(event.currentTarget.currentTime); }}
                    onEnded={() => { setActiveFile(null); setElapsed(0); }}
                  />
                  <button type="button" onClick={() => void toggle(media)} aria-label={`${active ? "Pause" : "Play"} ${media.title}`}>
                    {active ? <FaPause /> : <FaPlay />}
                    <span>{active ? `${formatTime(elapsed)} / ${formatTime(media.durationSeconds)}` : "Play"}</span>
                  </button>
                </td>
              </tr>;
            })}</tbody>
          </table></div>}
        </section>
      </main>
      <a className="public-demo-build" href="/app#/signup">Start building <FaArrowRight /></a>
      {betaOpen && <div className="public-demo-modal-backdrop" role="presentation" onMouseDown={() => setBetaOpen(false)}>
        <section className="public-demo-modal" role="dialog" aria-modal="true" aria-labelledby="beta-modal-title" onMouseDown={(event) => event.stopPropagation()}>
          <button className="public-demo-modal-close" type="button" onClick={() => setBetaOpen(false)} aria-label="Close"><FaTimes /></button>
          <span className="public-demo-eyebrow">ANDROID App</span>
          <h2 id="beta-modal-title">Get Signals Offline Demo App</h2>
          <p>Please provide your email to access the demo.</p>
          <form onSubmit={joinBeta}>
            <label htmlFor="beta-email">Google account email</label>
            <input id="beta-email" type="email" value={betaEmail} onChange={(event) => setBetaEmail(event.target.value)} placeholder="you@example.com" autoComplete="email" required autoFocus />
            {betaError && <p className="public-demo-beta-error" role="alert">{betaError}</p>}
            <button type="submit" disabled={submittingBeta}><FaGooglePlay /> {submittingBeta ? "Opening Play Store…" : "Continue to Play Store"}</button>
          </form>
        </section>
      </div>}
      {apkOpen && <div className="public-demo-modal-backdrop" role="presentation" onMouseDown={() => setApkOpen(false)}>
        <section className="public-demo-modal" role="dialog" aria-modal="true" aria-labelledby="apk-modal-title" onMouseDown={(event) => event.stopPropagation()}>
          <button className="public-demo-modal-close" type="button" onClick={() => setApkOpen(false)} aria-label="Close"><FaTimes /></button>
          <span className="public-demo-eyebrow">ANDROID DEMO APK</span>
          <h2 id="apk-modal-title">Install the Signals demo</h2>
          <p>Android may ask you to allow installs from this source—your browser or the Files app. Enable <b>Allow from this source</b> only to install this APK.</p>
          <a className="public-demo-apk-download" href="/api/public-demo/android-apk" onClick={() => setApkOpen(false)}><FaDownload /> Download now</a>
        </section>
      </div>}
    </div>
  );
}
